/** Web MIDI API input handling — connects hardware MIDI controllers to the synth.
 *
 * Manages MIDI device enumeration, note on/off handling, sustain pedal,
 * pitch bend, and CC (Control Change) message routing.
 */

/** A connected MIDI input device. */
export type MidiInputDevice = {
	readonly id: string;
	readonly name: string;
	readonly manufacturer: string;
};

/** A MIDI CC message. */
export type MidiCCMessage = {
	readonly channel: number;
	readonly cc: number;
	readonly value: number;
};

/** A MIDI note message. */
export type MidiNoteMessage = {
	readonly channel: number;
	readonly note: number;
	readonly velocity: number;
};

/** A MIDI pitch bend message. */
export type MidiPitchBendMessage = {
	readonly channel: number;
	/** Pitch bend value normalized to -1..+1. */
	readonly value: number;
};

/** Common MIDI CC numbers. */
export const MIDI_CC = {
	MODULATION: 1,
	BREATH: 2,
	VOLUME: 7,
	PAN: 10,
	EXPRESSION: 11,
	SUSTAIN_PEDAL: 64,
	RESONANCE: 71,
	RELEASE: 72,
	ATTACK: 73,
	CUTOFF: 74,
	DECAY: 75,
	SUSTAIN_LEVEL: 76,
} as const;

/** Default CC-to-parameter mappings. */
export const DEFAULT_CC_MAP: Readonly<Record<number, string>> = {
	[MIDI_CC.VOLUME]: "masterGain",
	[MIDI_CC.CUTOFF]: "filter.cutoff",
	[MIDI_CC.RESONANCE]: "filter.resonance",
	[MIDI_CC.ATTACK]: "adsr.attack",
	[MIDI_CC.DECAY]: "adsr.decay",
	[MIDI_CC.SUSTAIN_LEVEL]: "adsr.sustain",
	[MIDI_CC.RELEASE]: "adsr.release",
} as const;

/** Callbacks for MIDI input events. */
export type MidiInputCallbacks = {
	readonly onNoteOn: (msg: MidiNoteMessage) => void;
	readonly onNoteOff: (msg: MidiNoteMessage) => void;
	readonly onCC: (msg: MidiCCMessage) => void;
	readonly onPitchBend: (msg: MidiPitchBendMessage) => void;
	readonly onSustainPedal: (pressed: boolean) => void;
	readonly onDeviceChange: (devices: readonly MidiInputDevice[]) => void;
};

/** Active MIDI input connection. */
export type MidiInputConnection = {
	readonly disconnect: () => void;
	readonly getDevices: () => readonly MidiInputDevice[];
};

/** Check if Web MIDI API is available. */
export function isMidiAvailable(): boolean {
	return typeof navigator !== "undefined" && "requestMIDIAccess" in navigator;
}

/** Enumerate connected MIDI input devices from a MIDIAccess object. */
function enumerateInputDevices(access: MIDIAccess): readonly MidiInputDevice[] {
	const devices: MidiInputDevice[] = [];
	access.inputs.forEach((input) => {
		devices.push({
			id: input.id,
			name: input.name ?? "Unknown Device",
			manufacturer: input.manufacturer ?? "Unknown",
		});
	});
	return devices;
}

/** Parse a raw MIDI message into a typed event. */
function parseMidiMessage(
	data: Uint8Array,
	callbacks: MidiInputCallbacks,
): void {
	if (data.length < 1) return;

	const status = data[0] ?? 0;
	// MIDI wire channels are 0-indexed (0–15); convert to 1-indexed (1–16)
	// to match the MidiChannel type used throughout the app.
	const channel = (status & 0x0f) + 1;
	const messageType = status & 0xf0;

	switch (messageType) {
		case 0x90: {
			// Note On
			const note = data[1] ?? 0;
			const velocity = data[2] ?? 0;
			if (velocity > 0) {
				callbacks.onNoteOn({ channel, note, velocity });
			} else {
				// velocity 0 = note off
				callbacks.onNoteOff({ channel, note, velocity: 0 });
			}
			break;
		}
		case 0x80: {
			// Note Off
			const note = data[1] ?? 0;
			const velocity = data[2] ?? 0;
			callbacks.onNoteOff({ channel, note, velocity });
			break;
		}
		case 0xb0: {
			// Control Change
			const cc = data[1] ?? 0;
			const value = data[2] ?? 0;
			if (cc === MIDI_CC.SUSTAIN_PEDAL) {
				callbacks.onSustainPedal(value >= 64);
			}
			callbacks.onCC({ channel, cc, value });
			break;
		}
		case 0xe0: {
			// Pitch Bend
			const lsb = data[1] ?? 0;
			const msb = data[2] ?? 0;
			const raw = (msb << 7) | lsb;
			// Normalize to -1..+1: center at 8192 (0x2000)
			const normalized = (raw - 8192) / 8192;
			callbacks.onPitchBend({ channel, value: normalized });
			break;
		}
		// Other message types ignored
	}
}

/** Connect to all available MIDI input devices. */
export async function connectMidiInputs(
	callbacks: MidiInputCallbacks,
): Promise<MidiInputConnection> {
	if (!isMidiAvailable()) {
		return {
			disconnect: () => {},
			getDevices: () => [],
		};
	}

	const access = await navigator.requestMIDIAccess({ sysex: false });

	const onMidiMessage = (event: MIDIMessageEvent) => {
		if (event.data) {
			parseMidiMessage(event.data, callbacks);
		}
	};

	/** Attach listeners to all current inputs. */
	const attachListeners = () => {
		access.inputs.forEach((input) => {
			input.onmidimessage = onMidiMessage;
		});
	};

	/** Detach listeners from all inputs. */
	const detachListeners = () => {
		access.inputs.forEach((input) => {
			input.onmidimessage = null;
		});
	};

	// Initial device setup
	attachListeners();
	callbacks.onDeviceChange(enumerateInputDevices(access));

	// Watch for hot-plug/unplug
	access.onstatechange = () => {
		detachListeners();
		attachListeners();
		callbacks.onDeviceChange(enumerateInputDevices(access));
	};

	return {
		disconnect() {
			detachListeners();
			access.onstatechange = null;
		},
		getDevices() {
			return enumerateInputDevices(access);
		},
	};
}

/** Convert a MIDI CC value (0–127) to a normalized float (0–1). */
export function ccToNormalized(value: number): number {
	return Math.max(0, Math.min(1, value / 127));
}

/** Convert a MIDI CC value (0–127) to a parameter value within a given range. */
export function ccToRange(value: number, min: number, max: number): number {
	return min + ccToNormalized(value) * (max - min);
}

/** Convert a MIDI CC value (0–127) to a logarithmic frequency (20–20000 Hz). */
export function ccToLogFrequency(
	value: number,
	minHz = 20,
	maxHz = 20000,
): number {
	const norm = ccToNormalized(value);
	const minLog = Math.log(minHz);
	const maxLog = Math.log(maxHz);
	return Math.exp(minLog + norm * (maxLog - minLog));
}
